import {
  homeV2EyebrowClass,
  homeV2EyebrowStyle,
  homeV2Glass,
  homeV2Semantic,
  homeV2Text,
  homeV2Type,
} from '@/lib/home/homeV2Theme'
import type { CSSProperties } from 'react'

/** Warm glass surface for legacy Parks cards over section photography. */
export const parksV2GlassSurface: CSSProperties = {
  background: homeV2Glass.surface.background,
  border: `1px solid ${homeV2Glass.surface.border}`,
  boxShadow: homeV2Glass.surface.shadow,
  backdropFilter: `blur(${homeV2Glass.blur})`,
  WebkitBackdropFilter: `blur(${homeV2Glass.blur})`,
}

/** Champagne typography ladder for Parks over photo backgrounds. */
export const parksV2Type = {
  headline: homeV2Type(homeV2Text.headline),
  primary: homeV2Type(homeV2Text.primary),
  body: homeV2Type(homeV2Text.body),
  secondary: homeV2Type(homeV2Text.secondary),
  metadata: homeV2Type(homeV2Text.metadata),
  eyebrow: homeV2EyebrowStyle(),
} as const

export const parksV2EyebrowClass = homeV2EyebrowClass

export const parksV2IconMuted = homeV2Text.metadata
export const parksV2Divider = 'rgba(255, 236, 210, 0.18)'

/** Secondary support chips (Nearby help) — whisper glass, readable on photo backgrounds. */
export const parksV2HelpChipStyle: CSSProperties = {
  background: homeV2Glass.whisper.background,
  border: `1px solid ${homeV2Glass.whisper.border}`,
  boxShadow: homeV2Glass.whisper.shadow,
  backdropFilter: `blur(${homeV2Glass.blur})`,
  WebkitBackdropFilter: `blur(${homeV2Glass.blur})`,
  color: homeV2Text.secondary,
}

/** Nested “Why this park?” explanation — dark olive glass over Parks photography. */
export const parksV2WhyExplanationSurface: CSSProperties = {
  background: 'rgba(28, 22, 14, 0.42)',
  border: `1px solid rgba(255, 236, 210, 0.22)`,
  boxShadow:
    'inset 0 1px 0 rgba(255, 236, 210, 0.12), 0 4px 16px rgba(0, 0, 0, 0.18)',
  backdropFilter: `blur(${homeV2Glass.blur})`,
  WebkitBackdropFilter: `blur(${homeV2Glass.blur})`,
}

export const parksV2WhyIconColor = homeV2Text.warm

export const parksV2WhyToggleStyle: CSSProperties = {
  ...homeV2Type(homeV2Text.link),
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  textDecorationColor: 'rgba(255, 236, 210, 0.45)',
}

/** Reduced visual weight for secondary park actions (check-in, directions). */
export const parksV2SecondaryActionClass =
  '!min-h-[32px] !px-3 !py-1.5 !text-[11px] !font-medium !shadow-none'

/** Smart Walk — ~10% lighter than secondary actions. */
export const parksV2SmartWalkActionClass =
  '!min-h-[29px] !px-3 !py-1 !text-[10px] !font-medium !shadow-none !brightness-[0.96]'

/** Nested report / status row on V2 glass. */
export const parksV2NestedRowStyle: CSSProperties = {
  background: 'rgba(255, 255, 255, 0.06)',
  border: `1px solid ${parksV2Divider}`,
  borderRadius: 16,
}

/** Collapsible disclosure shell (Why this score, etc.). */
export const parksV2DisclosureSurface: CSSProperties = {
  ...parksV2WhyExplanationSurface,
  borderRadius: 16,
}

export function parksV2WarningCalloutStyle(): CSSProperties {
  return {
    border: '1px solid rgba(251, 191, 36, 0.35)',
    background: 'rgba(251, 191, 36, 0.12)',
    color: homeV2Text.body,
  }
}

export function parksV2ErrorBannerStyle(): CSSProperties {
  return {
    border: '1px solid rgba(248, 113, 113, 0.35)',
    background: 'rgba(248, 113, 113, 0.12)',
    color: '#FECACA',
  }
}

export function parksV2HazardChipStyle(): CSSProperties {
  const t = homeV2Semantic.paw.high
  return {
    background: t.bg,
    border: `1px solid ${t.border}`,
    color: t.text,
  }
}

export function parksV2HeatRiskToken(risk: string | null | undefined) {
  const key = (risk ?? 'low').toLowerCase()
  if (key === 'extreme') return homeV2Semantic.heat.extreme
  if (key === 'very_high') return homeV2Semantic.heat.very_high
  if (key === 'high') return homeV2Semantic.heat.high
  if (key === 'moderate') return homeV2Semantic.heat.moderate
  return homeV2Semantic.paw.low
}

export function parksV2LightingToken(score: number | null | undefined) {
  if (score == null) return homeV2Semantic.heat.unknown
  if (score >= 75) return homeV2Semantic.paw.low
  if (score >= 50) return homeV2Semantic.heat.moderate
  return homeV2Semantic.paw.high
}
